import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  ADMIN_MAIN_ROUTE,
  ADMIN_ROUTES,
} from '../../constants/admin.routing.constants';
import { EMPLOYEE_ROUTES } from '../../constants/employee.routing.constants';
import {
  OnboardingChecklist,
  OnboardingStepKey,
} from '../../services/onboarding/onboarding.model';
import { OnboardingService } from '../../services/onboarding/onboarding.service';

@Component({
  selector: 'lib-getting-started-card',
  imports: [RouterLink, TranslocoPipe],
  templateUrl: './getting-started-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GettingStartedCardComponent {
  private readonly onboardingService = inject(OnboardingService);

  readonly checklist = signal<OnboardingChecklist | null>(null);
  readonly visible = computed(() => {
    const checklist = this.checklist();
    return (
      !!checklist && !checklist.dismissed && checklist.steps.some(s => !s.done)
    );
  });
  // Whole URL strings, not command arrays: the route constants contain slashes, and RouterLink
  // only splits slashes in the first command, so ['/', 'a/b'] navigates to the unmatched /a%2Fb.
  readonly links: Record<OnboardingStepKey, string> = {
    profile: `/${EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.EDIT_PROFILE}`,
    invite_teammate: `/${ADMIN_MAIN_ROUTE}/${ADMIN_ROUTES.USERS_DATABASE.FORM}`,
    first_vendor: `/${EMPLOYEE_ROUTES.VENDORS.FORM}`,
    first_project: `/${EMPLOYEE_ROUTES.PROJECTS.LIST}`,
  };

  constructor() {
    this.onboardingService
      .checklist()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: res => this.checklist.set(res.data ?? null),
        error: () => this.checklist.set(null),
      });
  }

  dismiss(): void {
    this.onboardingService
      .dismissChecklist()
      .subscribe(() =>
        this.checklist.update(c => (c ? { ...c, dismissed: true } : c))
      );
  }
}
